/**
 * FILE-OVERVIEW:
 * - 役割: メール取り込みAPI（MailApi Lambda）への通信をまとめる。
 * - 主な連携: components/Memo/MailTab.tsx, backend/functions/MailApi
 * - 本番運用上の注意: ここの型は
 *   backend/functions/MailApi/src/lib/types.ts と対になっている。
 *   **項目名がずれると実行時まで気づけない。** 片方を変えたら必ず両方直すこと。
 *   CRA が src の外を import できないため、型を共有できず複製している。
 */

import { fetchAuthSession } from 'aws-amplify/auth';

/** 一覧の1行 */
export interface MailListRow {
  mailId: string;
  subject: string;
  /** 表示用。`名前 <アドレス>` の形 */
  from: string;
  /** 絞り込み用。小文字に揃えたアドレス */
  fromAddress: string;
  receivedAt: string;
  preview: string;
  attachmentCount: number;
  /** PDF以外で保存しなかった添付の件数 */
  rejectedAttachmentCount: number;
  /** 自分が読んだか。ユーザーごとに違う */
  isRead: boolean;
}

/** 保存しなかった添付。届いた事実だけ残っている */
export interface RejectedAttachment {
  fileName: string;
  contentType: string;
  size: number;
}

/** 添付PDF。url は有効期限付きの署名付きURL */
export interface MailAttachment {
  fileName: string;
  s3Key: string;
  size: number;
  url: string;
}

/** 詳細 */
export interface MailDetail extends MailListRow {
  to: string;
  hasHtmlBody: boolean;
  bodyText: string;
  /** HTML本文の署名付きURL。表示するならサニタイズが要る */
  bodyHtmlUrl: string | null;
  attachments: MailAttachment[];
  rejectedAttachments: RejectedAttachment[];
}

export interface MailListResponse {
  items: MailListRow[];
  /** 次ページの位置。null なら最後 */
  cursor: string | null;
}

export interface UnreadCount {
  unread: number;
  scanned: number;
  limit: number;
}

export interface MailPreferences {
  notifyEnabled: boolean;
  updatedAt: string;
}

const API_URL = process.env.REACT_APP_MAIL_API_URL;

/**
 * 認証ヘッダを作る。
 * API Gateway の Cognito オーソライザは ID トークンを見るため、
 * calendarApi.ts と同じく idToken を優先する。
 */
const getHeaders = async (): Promise<Record<string, string>> => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString() ?? session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('有効な認証トークンが取得できません');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

/** APIのベースURL。未設定なら呼び出し側で分かるように投げる */
const baseUrl = (): string => {
  if (!API_URL) {
    throw new Error('REACT_APP_MAIL_API_URL が設定されていません。環境変数を確認してください');
  }
  return API_URL;
};

/**
 * 応答を検査して JSON にする。
 * エラー本文はそのまま画面に出さず、状況が分かる日本語に置き換える。
 */
const parse = async <T, >(res: Response, what: string): Promise<T> => {
  if (res.ok) {
    return res.json() as Promise<T>;
  }
  if (res.status === 401 || res.status === 403) {
    throw new Error(`${what}の権限がありません。再ログインしてください`);
  }
  if (res.status === 404) {
    throw new Error('メールが見つかりません。すでに削除された可能性があります');
  }
  throw new Error(`${what}に失敗しました（${res.status}）`);
};

/**
 * 一覧を受信日時の新しい順で取得する。
 *
 * @param limit 取得件数（サーバ側の上限は50）
 * @param cursor 続きを読む位置。前回の応答の cursor を渡す
 */
export const fetchMailList = async (
  limit = 30,
  cursor?: string | null,
): Promise<MailListResponse> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) {
    params.set('cursor', cursor);
  }
  const res = await fetch(`${baseUrl()}?${params.toString()}`, {
    method: 'GET',
    headers: await getHeaders(),
  });
  return parse<MailListResponse>(res, 'メール一覧の取得');
};

/** 詳細を取得する。添付とHTML本文の署名付きURLが含まれる */
export const fetchMailDetail = async (mailId: string): Promise<MailDetail> => {
  const res = await fetch(`${baseUrl()}/${encodeURIComponent(mailId)}`, {
    method: 'GET',
    headers: await getHeaders(),
  });
  return parse<MailDetail>(res, 'メールの取得');
};

/** タブのバッジに出す未読件数 */
export const fetchUnreadCount = async (): Promise<UnreadCount> => {
  const res = await fetch(`${baseUrl()}/unread-count`, {
    method: 'GET',
    headers: await getHeaders(),
  });
  return parse<UnreadCount>(res, '未読件数の取得');
};

/** 既読・未読を切り替える */
export const setMailRead = async (mailId: string, isRead: boolean): Promise<void> => {
  const res = await fetch(`${baseUrl()}/${encodeURIComponent(mailId)}/read`, {
    method: isRead ? 'POST' : 'DELETE',
    headers: await getHeaders(),
  });
  await parse<unknown>(res, isRead ? '既読の記録' : '未読への変更');
};

/** 通知設定を読む */
export const fetchMailPreferences = async (): Promise<MailPreferences> => {
  const res = await fetch(`${baseUrl()}/preferences`, {
    method: 'GET',
    headers: await getHeaders(),
  });
  return parse<MailPreferences>(res, '通知設定の取得');
};

/** 通知設定を書く */
export const updateMailPreferences = async (notifyEnabled: boolean): Promise<MailPreferences> => {
  const res = await fetch(`${baseUrl()}/preferences`, {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify({ notifyEnabled }),
  });
  return parse<MailPreferences>(res, '通知設定の保存');
};

/**
 * 日本時間の年月日時分に分解する。
 *
 * 端末のタイムゾーンを使うと、出張先や設定次第で表示時刻がずれる。
 * 受信日時は取材の締切に直結するため、**端末に関係なく日本時間で出す**。
 */
const toJstParts = (d: Date): Record<string, string> => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const out: Record<string, string> = {};
  for (const part of parts) {
    out[part.type] = part.value;
  }
  return out;
};

/**
 * 一覧に出す受信日時。`09/13 08:12` の形（日本時間）。
 * 年をまたいだものだけ年を付ける。
 *
 * @param iso 受信日時（ISO8601）
 * @param now 「今年かどうか」の判定に使う基準。テストから固定できる
 */
export const formatReceivedAt = (iso: string, now = new Date()): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const t = toJstParts(d);
  // hour12:false でも環境により 24 時と出ることがあるため 00 に寄せる
  const hour = t.hour === '24' ? '00' : t.hour;
  const year = t.year === toJstParts(now).year ? '' : `${t.year}/`;
  return `${year}${t.month}/${t.day} ${hour}:${t.minute}`;
};
